import {
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  LoadingState,
  PORTAL_PAGE_TITLE_ID,
} from '@beautyburst/ui';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { AdminApiClient, AdminRecord, AdminStoreLocation } from '../api-client.ts';
import { AdminApiError } from '../http-client.ts';

const PAGE_SIZE = 100;

const ROLE_OPTIONS = [
  { value: 'admin.operations', label: 'Beauty Burst staff / admin' },
  { value: 'retailer.owner', label: 'Retailer owner' },
  { value: 'retailer.manager', label: 'Retailer manager' },
  { value: 'retailer.staff', label: 'Retailer staff' },
] as const;

function text(row: AdminRecord, key: string): string {
  const value = row[key];
  return value === null || value === undefined ? '—' : String(value);
}

function errorText(error: unknown): string {
  return error instanceof AdminApiError
    ? error.message
    : 'The administrator service did not return data. Try again.';
}

function roleLabel(roleKey: string): string {
  return ROLE_OPTIONS.find((role) => role.value === roleKey)?.label ?? roleKey;
}

function userStatus(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'SUSPENDED':
      return 'Suspended';
    case 'PENDING_VERIFICATION':
      return 'Pending verification';
    case 'DELETED':
      return 'Deleted';
    default:
      return status;
  }
}

function retailerOptions(stores: readonly AdminStoreLocation[]) {
  const byId = new Map<string, string>();
  for (const store of stores) byId.set(store.retailer.id, store.retailer.name);
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function PeopleAccessScreen({ client }: { readonly client: AdminApiClient }) {
  const [users, setUsers] = useState<readonly AdminRecord[] | null>(null);
  const [stores, setStores] = useState<readonly AdminStoreLocation[] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] =
    useState<(typeof ROLE_OPTIONS)[number]['value']>('admin.operations');
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const [detail, setDetail] = useState<{
    readonly user: AdminRecord;
    readonly roles: readonly AdminRecord[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setFailure(null);
    try {
      // User records are essential to this screen; a failure here is fatal.
      const userResult = await client.users.list({ limit: PAGE_SIZE, offset: 0 });
      setUsers(userResult.users);
      if (selectedUserId === '' && userResult.users[0] !== undefined)
        setSelectedUserId(text(userResult.users[0], 'id'));
      // Store names only populate the retailer-scoping dropdown. A stores failure
      // must not take down user management, so it is tolerated and logged.
      try {
        setStores(await client.stores.list());
      } catch {
        setStores([]);
      }
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (selectedUserId === '') {
      setDetail(null);
      return;
    }
    let active = true;
    void client.users.detail(selectedUserId).then(
      (value) => active && setDetail(value),
      (error: unknown) => active && setFailure(errorText(error)),
    );
    return () => {
      active = false;
    };
  }, [client, selectedUserId]);

  const retailers = useMemo(() => retailerOptions(stores ?? []), [stores]);
  const retailerRole = selectedRole.startsWith('retailer.');

  const submitGrant = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const reason = String(form.get('reason') ?? '').trim();
    if (selectedUserId === '' || reason === '' || (retailerRole && selectedRetailerId === ''))
      return;
    setBusy(true);
    setFailure(null);
    try {
      await client.users.grantRole(selectedUserId, {
        roleKey: selectedRole,
        reason,
        ...(retailerRole ? { retailerId: selectedRetailerId } : {}),
      });
      setDetail(await client.users.detail(selectedUserId));
      await load();
      event.currentTarget.reset();
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const revokeRole = async (roleKey: string) => {
    const reason = window.prompt(`Reason for revoking ${roleLabel(roleKey)} access?`)?.trim() ?? '';
    if (reason === '' || selectedUserId === '') return;
    setBusy(true);
    setFailure(null);
    try {
      await client.users.revokeRole(selectedUserId, roleKey, reason);
      setDetail(await client.users.detail(selectedUserId));
      await load();
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const suspendUser = async () => {
    if (detail === null) return;
    const reason =
      window.prompt(`Reason for suspending ${text(detail.user, 'email')}?`)?.trim() ?? '';
    if (reason === '' || selectedUserId === '') return;
    setBusy(true);
    setFailure(null);
    try {
      await client.users.suspend(selectedUserId, reason);
      setDetail(await client.users.detail(selectedUserId));
      await load();
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const reactivateUser = async () => {
    if (detail === null) return;
    const reason =
      window.prompt(`Reason for reactivating ${text(detail.user, 'email')}?`)?.trim() ?? '';
    if (reason === '' || selectedUserId === '') return;
    setBusy(true);
    setFailure(null);
    try {
      await client.users.reactivate(selectedUserId, reason);
      setDetail(await client.users.detail(selectedUserId));
      await load();
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  if (loading && users === null) return <LoadingState label="Loading people and access" />;

  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Manage Beauty Burst and retailer access with names and human-readable roles. Internal IDs
        stay behind the interface.
      </p>
      {failure === null ? null : (
        <ErrorState
          title="People and access unavailable"
          description={failure}
          retry={<Button onClick={() => void load()}>Try again</Button>}
        />
      )}

      <Card>
        <h2>Add access</h2>
        <form className="admin-inline-form" onSubmit={(event) => void submitGrant(event)}>
          <label htmlFor="people-access-user">Person</label>
          <select
            id="people-access-user"
            name="userId"
            value={selectedUserId}
            onChange={(event) => setSelectedUserId(event.currentTarget.value)}
            required
          >
            <option value="">Select a person</option>
            {(users ?? []).map((user) => (
              <option key={text(user, 'id')} value={text(user, 'id')}>
                {text(user, 'full_name')} — {text(user, 'email')}
              </option>
            ))}
          </select>
          <label>
            Role
            <select
              name="roleKey"
              value={selectedRole}
              onChange={(event) => {
                const value = event.currentTarget.value as (typeof ROLE_OPTIONS)[number]['value'];
                setSelectedRole(value);
                if (!value.startsWith('retailer.')) setSelectedRetailerId('');
              }}
              required
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </label>
          {retailerRole ? (
            <label>
              Retailer
              <select
                name="retailerId"
                value={selectedRetailerId}
                onChange={(event) => setSelectedRetailerId(event.currentTarget.value)}
                required
              >
                <option value="">Select a retailer</option>
                {retailers.map((retailer) => (
                  <option key={retailer.id} value={retailer.id}>
                    {retailer.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <Field id="people-access-reason" name="reason" label="Reason" required />
          <Button type="submit" disabled={busy} style={{ minWidth: '44px', minHeight: '44px' }}>
            {busy ? 'Saving…' : 'Add access'}
          </Button>
        </form>
      </Card>

      {detail === null ? null : (
        <Card>
          <h2>{text(detail.user, 'full_name')}</h2>
          <p>
            {text(detail.user, 'email')} — {userStatus(text(detail.user, 'status'))}
          </p>
          <Button
            type="button"
            variant={text(detail.user, 'status') === 'SUSPENDED' ? 'primary' : 'danger'}
            disabled={busy}
            onClick={() =>
              void (text(detail.user, 'status') === 'SUSPENDED' ? reactivateUser() : suspendUser())
            }
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            {text(detail.user, 'status') === 'SUSPENDED' ? 'Reactivate account' : 'Suspend account'}
          </Button>
          <DataTable
            caption="Active access for selected person"
            rows={detail.roles}
            rowKey={(role) => text(role, 'id')}
            emptyMessage="This person has no active roles."
            columns={[
              {
                key: 'role',
                header: 'Access',
                render: (role) => roleLabel(text(role, 'role_key')),
              },
              {
                key: 'retailer',
                header: 'Retailer scope',
                render: (role) => {
                  const retailerId = text(role, 'retailer_id');
                  if (retailerId === '—') return 'Beauty Burst platform';
                  return (
                    retailers.find((retailer) => retailer.id === retailerId)?.name ?? 'Retailer'
                  );
                },
              },
              {
                key: 'action',
                header: 'Action',
                render: (role) => (
                  <Button
                    type="button"
                    variant="danger"
                    disabled={busy}
                    onClick={() => void revokeRole(text(role, 'role_key'))}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                  >
                    Revoke access
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}
    </section>
  );
}
